import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import type { LoginResponse } from '@/api/types/auth';
import RouterAwareErrorBoundary from '@/components/app-shell/RouterAwareErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import posthog, { normalizeUserEmail, registerUserEmail } from '@/config/analytics/posthog';
import { identifySentryUser } from '@/config/monitoring/sentry';
import { queryKeys } from '@/config/query/queryKeys';
import { MailboxMismatchScreen } from '@/features/auth/components/MailboxMismatchScreen';
import { getStoredDeployment } from '@/utils/apiBaseUrl';
import { setNavigateRef } from '@/utils/navigationRef';
import { useAuthStore } from '@/utils/stores/useAuthStore';

import { ProtectedRoute } from './components/app-shell/ProtectedRoute';
import { RouteErrorBoundary } from './components/app-shell/RouteErrorBoundary';
import {
  addItemChangedHandler,
  getOutlookContext,
  initializeOffice,
  removeItemChangedHandler,
  removeSelectedItemChangedHandler,
  selectedItemChangedHandler,
} from './config/office/office';
import { AssistantPage } from './pages/AssistantPage';
import { ConnectMailbox } from './pages/ConnectMailbox';
import { Forbidden } from './pages/Forbidden';
import { HistoryPage } from './pages/HistoryPage';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { NarrativePage } from './pages/NarrativePage';
import { ProfilePage } from './pages/Profile';
import { RecorderPage } from './pages/Recorder';
import ServiceUnavailablePage from './pages/ServiceUnavailablePage';
import { ROUTES } from './utils/routeUtils';
import { useOfficeContextStore } from './utils/stores/useOfficeContextStore';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const initializeStore = useOfficeContextStore((state) => state.initialize);
  const { accountMismatch } = useAuthStore();
  const { data: user } = useQuery<LoginResponse['user']>({
    enabled: false,
    queryKey: queryKeys.auth.user(),
  });
  const clerkId = user?.clerkId;
  const deployment = getStoredDeployment();

  useEffect(() => {
    if (!clerkId) {
      return;
    }

    // Normalized before it reaches any sink. `identify`'s second argument is a
    // `$set` on the person's `email` — the same field the backend `$set`s
    // (lowercased) on every AI event, so raw casing here would make that field
    // flip on every write.
    const email = normalizeUserEmail(user?.email);

    posthog.identify(clerkId, {
      deployment_env: deployment ?? undefined,
      email,
      name: user?.displayName,
      product: 'outlook_addin',
    });

    registerUserEmail(email);

    // Same identity into Sentry, so an error can be traced to a user (and a
    // region) instead of only a timestamp.
    identifySentryUser({
      clerkId,
      deployment: deployment ?? undefined,
      displayName: user?.displayName,
      email,
      userId: user?.id,
    });
  }, [clerkId, deployment, user?.displayName, user?.email, user?.id]);

  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      route: location.pathname,
    });
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    let isMounted = true;
    let pollingInterval: number | null = null;
    let lastKnownItemId: string | null = null;
    let lastKnownConversationId: string | null = null;
    let lastEventTime = 0;

    const handleContextChange = (newItemId: string | null, conversationId: string | null) => {
      // Avoid duplicate updates for the same ID
      if (newItemId === lastKnownItemId && conversationId === lastKnownConversationId) {
        return;
      }
      lastKnownItemId = newItemId;
      lastKnownConversationId = conversationId;
      lastEventTime = Date.now();

      useOfficeContextStore.getState().setSelectedCustomEmails([]);
      // Re-initialize store to update context and current email
      initializeStore();
    };

    // Authentication will happen via MSAL when user clicks login
    initializeOffice()
      .then(() => {
        if (!isMounted) {
          return;
        }

        // Initialize the global store (gets context, fetches email if applicable)
        initializeStore();

        // Get initial item ID
        const initialContext = useOfficeContextStore.getState().contextInfo;
        lastKnownItemId = initialContext.itemId;

        // Register Handlers
        addItemChangedHandler(handleContextChange);
        selectedItemChangedHandler(handleContextChange);

        // Hybrid approach: Polling fallback for platforms where events don't fire
        pollingInterval = window.setInterval(() => {
          // Don't poll if the page is hidden (saves resources)
          if (document.hidden) {
            return;
          }

          // If event fired recently (last 2 seconds), skip this poll to avoid double-processing
          if (Date.now() - lastEventTime < 2000) {
            return;
          }

          // Check if the context has changed by querying Office.js directly
          const currentContext = getOutlookContext();

          // Triggers update if ID changed
          if (
            currentContext.itemId !== lastKnownItemId ||
            currentContext.conversationId !== lastKnownConversationId
          ) {
            handleContextChange(currentContext.itemId, currentContext.conversationId);
          }
        }, 1000); // Poll every 1 second
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Office initialization failed:', error);
        }
      });

    // Cleanup: remove event handler and stop polling on unmount
    return () => {
      isMounted = false;
      removeItemChangedHandler();
      removeSelectedItemChangedHandler();

      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, []);

  // Register the navigate function so it can be used outside React (e.g. QueryClient callbacks)
  useEffect(() => {
    setNavigateRef(navigate);
  }, [navigate]);

  // Continuous account-mismatch detection: the Outlook host mailbox is
  // compared against the connected user's server-side alias list. The check
  // is idempotent and cheap (sync store + Office.context reads), so
  // over-firing is harmless. Triggers: the connected-user profile arriving or
  // changing (see the `user` effect below) and Office context updates (covers
  // Office.js becoming ready after the profile loaded).
  useEffect(() => {
    const { checkAccountMismatch } = useAuthStore.getState();

    // Run once now in case the office context is already ready.
    checkAccountMismatch();

    // Re-check on Office context store changes, but only when the host
    // mailbox identity could have actually shifted. The store updates at
    // ~1 Hz from the polling loop in App; firing checkAccountMismatch on
    // every tick is wasteful since the mailbox SMTP rarely changes within a
    // session. Track the `isInitialized` transition and `contextInfo.itemId`
    // (proxy for "the host might have switched mailboxes") and skip otherwise.
    let lastInitialized = useOfficeContextStore.getState().isInitialized;
    let lastItemId = useOfficeContextStore.getState().contextInfo.itemId;
    const unsubscribeOfficeContext = useOfficeContextStore.subscribe((state) => {
      const initializedChanged = state.isInitialized !== lastInitialized;
      const itemChanged = state.contextInfo.itemId !== lastItemId;
      if (initializedChanged || itemChanged) {
        lastInitialized = state.isInitialized;
        lastItemId = state.contextInfo.itemId;
        checkAccountMismatch();
      }
    });

    return () => {
      unsubscribeOfficeContext();
    };
  }, []);

  // Re-run the mismatch check whenever the connected-user profile changes —
  // it's the server-truth side of the comparison.
  useEffect(() => {
    useAuthStore.getState().checkAccountMismatch();
  }, [user]);

  if (accountMismatch) {
    return <MailboxMismatchScreen />;
  }

  return (
    <RouterAwareErrorBoundary>
      <>
        <Routes>
          <Route element={<Navigate replace to={ROUTES.HOME} />} path="/" />
          <Route element={<Navigate replace to={ROUTES.HOME} />} path="/taskpane.html" />
          <Route
            element={
              <RouteErrorBoundary>
                <Login />
              </RouteErrorBoundary>
            }
            path={ROUTES.LOGIN}
          />
          <Route
            element={
              <RouteErrorBoundary>
                <ConnectMailbox />
              </RouteErrorBoundary>
            }
            path={ROUTES.CONNECT_MAILBOX}
          />
          <Route
            element={
              <RouteErrorBoundary>
                <Forbidden />
              </RouteErrorBoundary>
            }
            path={ROUTES.FORBIDDEN}
          />
          <Route element={<ServiceUnavailablePage />} path={ROUTES.SERVICE_UNAVAILABLE} />
          <Route element={<ProtectedRoute redirectPath={ROUTES.LOGIN} />}>
            <Route element={<Home />} path={ROUTES.HOME}>
              {/* Nested routes - paths are relative to /home */}
              <Route
                element={
                  <RouteErrorBoundary>
                    <AssistantPage />
                  </RouteErrorBoundary>
                }
                path="assistant/:sessionId?"
              />
              <Route
                element={
                  <RouteErrorBoundary>
                    <NarrativePage />
                  </RouteErrorBoundary>
                }
                path="narrative"
              />
              <Route
                element={
                  <RouteErrorBoundary>
                    <ProfilePage />
                  </RouteErrorBoundary>
                }
                path="profile"
              />
              <Route
                element={
                  <RouteErrorBoundary>
                    <HistoryPage />
                  </RouteErrorBoundary>
                }
                path="history"
              />
              <Route element={<Navigate replace to="assistant" />} index />
            </Route>
          </Route>
          <Route
            element={
              <RouteErrorBoundary>
                <RecorderPage />
              </RouteErrorBoundary>
            }
            path="/recorder"
          />
          {/* Catch-all route to redirect back home */}
          <Route element={<Navigate replace to={ROUTES.HOME} />} path="*" />
        </Routes>
        {/* /recorder mounts its own top-center Toaster; sonner Toasters share
            one global store, so a second mount would render every toast twice. */}
        {!location.pathname.startsWith('/recorder') && <Toaster closeButton />}
      </>
    </RouterAwareErrorBoundary>
  );
}

export default App;
